import 'url-search-params-polyfill'
import React from 'react'
import { CssBaseline, ThemeProvider } from '@mui/material'
import { FC } from 'react'
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router'
import { RootContainer } from '../Containers/RootContainer'
import {PrefillValues} from '../interfaces'
import { ErrorContainer } from '../Containers/ErrorContainer'
import { PrivacyPolicyContainer } from '../Containers/PrivacyPolicyContainer'
import { ReservationDoneContainer } from '../Containers/ReservationDoneContainer'
import { ServiceCardContainer } from '../Containers/ServiceCardContainer'
import { CancelledReservationContainer } from '../Containers/CancelledReservationContainer'
import { PaymentContainer } from '../Containers/PaymentContainer'
import {useAppTheme, useAppThemeName, useSelfServiceTheme, useSelfServiceThemeName} from './hooks'
import {ReminderSettingsContainer} from '../Containers/ReminderSettingsContainer'
import {SelfServiceContainer} from "../Containers/SelfServiceContainer"

interface SubRouteProps {
    embedded: boolean
    setGtmIds: (chainGtmId: string) => void
}

interface RootRouteProps extends SubRouteProps {
    prefill?: PrefillValues
    scrollContainerRef?: any
}

export const MuiProviders: FC<any> = ({ children, theme }) => {
    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            {children}
        </ThemeProvider>
    )
}

const CheckInRoutes: FC<SubRouteProps> = ({ embedded , setGtmIds}): JSX.Element => {
    const themeName = useAppThemeName()
    const theme = useAppTheme()
    return (
        <MuiProviders theme={theme}>
            <ServiceCardContainer
                embedded={embedded}
                themeName={themeName}
                setGtmIds={setGtmIds}
            />
        </MuiProviders>
    )
}

const ReminderSettingsRoutes: FC<SubRouteProps> = ({ embedded , setGtmIds}) => {
    const themeName = useAppThemeName()
    const theme = useAppTheme()
    return (
        <MuiProviders theme={theme}>
            <Routes>
                <Route
                    path=""
                    element={
                        <ReminderSettingsContainer
                            embedded={embedded}
                            themeName={themeName}
                            setGtmIds={setGtmIds}
                        />
                    }
                />
                <Route element={<ErrorContainer />} />
            </Routes>
        </MuiProviders>
    )
}


const ReservationDone: FC<RootRouteProps> = ({ embedded, setGtmIds }) => {
    const theme = useAppTheme()
    return (
        <MuiProviders theme={theme}>
            <ReservationDoneContainer embedded={embedded} setGtmIds={setGtmIds} />
        </MuiProviders>
    )
}

const ReservationMain: FC<RootRouteProps> = ({ embedded, setGtmIds, prefill, scrollContainerRef}) => {
    const themeName = useAppThemeName()
    const theme = useAppTheme()
    return (
        <MuiProviders theme={theme}>
            <RootContainer
                embedded={embedded}
                themeName={themeName}
                setGtmIds={setGtmIds}
                scrollContainerRef={scrollContainerRef}
                prefill={prefill}
            />
        </MuiProviders>
    )
}


const SelfServiceRoutes: FC<RootRouteProps> = () => {
    const themeName = useSelfServiceThemeName()
    const theme = useSelfServiceTheme()
    return (
        <MuiProviders theme={theme}>
            <SelfServiceContainer
                            themeName={themeName}
                        />
        </MuiProviders>
    )
}

interface RouteProps {
    setGtmIds: (chainGtmId: string) => void
}

const ReservationRedirect = () => {
    const location = useLocation()
    const path = location.pathname
    const newUrl = path.replace('/chain/', '/').replace('/station/', '/')

    console.log('rewrite', path, newUrl)

    return <Navigate to={newUrl} />
}

const ShortcutRedirect = () => {
    const {part1, part2} = useParams()

    if (part1.match('^[A-Za-z0-9]{8}$') && !part2) {
        return <Navigate to={"/check-in?token=" + part1} />
    } else if (part1.match('^[0-9]+$') && !part2) {
        return <Navigate to={"/booking/" + part1}/>
    } else if (part1.match('^[0-9]+$') && part2.match('^[0-9]+$')) {
        return <Navigate to={"/booking/" + part1 + '/' + part2}/>
    }

    return <ErrorContainer />
}

export const MainRoutes: FC<RouteProps> = ({ setGtmIds}) => {
    const reminderEnabled = false
    return (
        <Routes>
            <Route
                path="/self-service"
                element={<SelfServiceRoutes
                        embedded={false}
                        setGtmIds={setGtmIds}
                    />}
            />
            <Route
                path="/chain/:chainId/:stationId/cancelled-reservation"
                element={<CancelledReservationContainer embedded={false} setGtmIds={setGtmIds} />}
            />
            <Route
                path="/chain/:chainId/cancelled-reservation"
                element={<CancelledReservationContainer embedded={false} setGtmIds={setGtmIds} />}
            />
            <Route
                path="/error"
                element={
                    <ErrorContainer />
                }
            />
            <Route
                path="/self-service/error"
                element={
                    <ErrorContainer />
                }
            />
            <Route
                path="/privacy-policy"
                element={
                    <PrivacyPolicyContainer />
                }
            />
            <Route
                path="/check-in/embed"
                element={
                    <CheckInRoutes embedded={true} setGtmIds={setGtmIds} />
                }
            />
            <Route
                path="/check-in"
                element={
                    <CheckInRoutes
                        embedded={false}
                        setGtmIds={setGtmIds}
                    />
                }
            />
            <Route path="/booking/chain/:chainId" element={<ReservationRedirect />}/>
            <Route path="/booking/chain/:chainId/embed" element={<ReservationRedirect />}/>
            <Route path="/booking/chain/:chainId/station/:stationId" element={<ReservationRedirect />}/>
            <Route path="/booking/chain/:chainId/station/:stationId/embed" element={<ReservationRedirect />}/>
            <Route
                path="/booking/:chainId/:stationId?"
                element={<ReservationMain embedded={false} setGtmIds={setGtmIds} />}
            />
            <Route
                path="/booking/:chainId/:stationId?/thank-you/:state?"
                element={<ReservationDone embedded={false} setGtmIds={setGtmIds} />}
            />
            <Route
                path="/booking/:chainId/:stationId?/embed"
                element={<ReservationMain embedded={true} setGtmIds={setGtmIds} />}
            />
            <Route
                path="/booking/:chainId/:stationId?/embed/thank-you/:state?"
                element={<ReservationDone embedded={true} setGtmIds={setGtmIds} />}
            />
            <Route
                path={'/quick-payment/thank-you/*'}
                element={
                    <ReservationDone
                        embedded={false}
                        setGtmIds={setGtmIds}
                    />
                }
            />
            <Route
                path={'/payment/*'}
                element={
                    <PaymentContainer
                        setGtmIds={setGtmIds}
                    />
                }
            />
            {reminderEnabled && <Route
                path="/reminder/:chainId/:stationId"
                element={
                    <ReminderSettingsRoutes
                        embedded={false}
                        setGtmIds={setGtmIds}
                    />
                }
            />}
            <Route
                path="/:part1/:part2?"
                element={<ShortcutRedirect />}
            />
            <Route path="*" element={<ErrorContainer />} />
        </Routes>
    )
}
